import { IonHeader, IonModal, IonToolbar } from "@ionic/react";
import classnames from "classnames";
import { ComponentProps, forwardRef, ForwardRefRenderFunction, ReactNode, useEffect, useRef, useState } from "react";

import CircleButton from "@components/v2/buttons/CircleButton";
import styles from "@components/v2/overlays/Modal.module.css";
import Text from "@components/v2/typography/Text";
import { Opener } from "@hooks/useOpener";

type ChildrenData = {
  modalDisplayed: boolean;
};

type Props = Omit<
  ComponentProps<typeof IonModal>,
  "children" | "isOpen" | "onDidDismiss" | "onDidPresent" | "onWillDismiss" | "onWillPresent"
> & {
  children: (data: ChildrenData) => ReactNode;
  contentClassName?: string;
  fullScreen?: boolean;
  fullWidth?: boolean;
  header?: ReactNode;
  opener: Opener;
  title?: string;
  useMobileStyle?: boolean;
  variant?: "default" | "community";
};

export type ModalElement = {
  scrollToTop: () => void;
};

const ModalSubcomponent: ForwardRefRenderFunction<ModalElement, Props> = ({
  children,
  className,
  contentClassName,
  fullScreen = false,
  fullWidth = false,
  header,
  onClick,
  onIonModalDidDismiss,
  onIonModalDidPresent,
  onIonModalWillDismiss,
  onIonModalWillPresent,
  opener,
  title,
  variant = "default",
  ...ionModalProps
}) => {
  const contentRef = useRef<HTMLDivElement>(null);

  const [modalDisplayed, setModalDisplayed] = useState<boolean>(false);
  const [isScrolled, setIsScrolled] = useState<boolean>(false);

  // dismiss the modal on unmount
  useEffect(
    () => () => {
      opener.close();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleScroll = (event: Event) => {
    const target = event.target as HTMLElement;
    const scrollTop = target.scrollTop;
    const scrollTopThreshold = 20;
    setIsScrolled(scrollTop > scrollTopThreshold);
  };

  useEffect(() => {
    if (!modalDisplayed) {
      setIsScrolled(false);
    }

    const content = contentRef.current;
    if (content) {
      content.addEventListener("scroll", handleScroll as EventListener);
      return () => {
        content.removeEventListener("scroll", handleScroll as EventListener);
      };
    }
  }, [modalDisplayed]);

  return (
    <IonModal
      {...ionModalProps}
      className={classnames(
        "component-Modal",
        styles.modal,
        className,
        fullScreen && styles.isFullScreen,
        styles[`variant-${variant}`]
      )}
      isOpen={opener.isOpen}
      onClick={event => {
        if (onClick) onClick(event);
        event.stopPropagation();
      }}
      onIonModalDidDismiss={event => {
        opener.close();
        setModalDisplayed(false);
        if (onIonModalDidDismiss) onIonModalDidDismiss(event);
      }}
      onIonModalDidPresent={event => {
        opener.open();
        setModalDisplayed(true);
        if (onIonModalDidPresent) onIonModalDidPresent(event);
      }}
      onIonModalWillDismiss={event => {
        setModalDisplayed(false);
        if (onIonModalWillDismiss) onIonModalWillDismiss(event);
      }}
      onIonModalWillPresent={event => {
        if (onIonModalWillPresent) onIonModalWillPresent(event);
      }}
    >
      <>
        <IonHeader className={classnames("ion-no-border", styles.header, isScrolled && styles.isScrolled)}>
          <IonToolbar className={styles.titleToolbar}>
            {header && <div className={styles.header}>{header}</div>}
            {!header && title && (
              <Text className={styles.title} tag="h2">
                {title}
              </Text>
            )}

            {variant === "community" ? (
              <CircleButton
                ariaLabelKey="accessibility.ariaLabels.close"
                className={styles.closeButton}
                color="gray-300"
                data-test="close-modal"
                fill="outline"
                icon="xmark"
                onClick={opener.close}
                size="large"
                slot="end"
              />
            ) : (
              <CircleButton
                ariaLabelKey="accessibility.ariaLabels.close"
                className={styles.closeButton}
                color="gray-300"
                data-test="close-modal"
                fill="clear"
                icon="xmark"
                onClick={opener.close}
                slot="end"
              />
            )}
          </IonToolbar>
        </IonHeader>
        <div
          className={classnames(styles.content, fullWidth && styles.isFullWidth, contentClassName && contentClassName)}
          ref={contentRef}
        >
          {children({ modalDisplayed })}
        </div>
      </>
    </IonModal>
  );
};

const Modal = forwardRef(ModalSubcomponent);

export default Modal;
